import React from 'react';
import ReactMarkdown from 'react-markdown';
import Avatar from '../ui/Avatar';
import JuliaSources from './JuliaSources';
import { JuliaMessageMetadata } from '../../types';
import { CHARACTERS } from '../../assets/characters';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  userName?: string;
  isTyping?: boolean;
  metadata?: JuliaMessageMetadata | null;
  /** Mode panneau étroit (aside) */
  compact?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  role, content, userName = 'Vous', isTyping = false, metadata, compact = false,
}) => {
  const isUser = role === 'user';
  const avatarSize = compact ? 26 : 32;

  return (
    <div
      style={{
        display: 'flex',
        gap: compact ? '0.45rem' : '0.75rem',
        padding: compact ? '0.4rem 0.35rem' : '0.75rem 1rem',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Avatar
        name={isUser ? userName : 'Julia'}
        size={avatarSize}
        src={isUser ? undefined : CHARACTERS.julia}
        fit={isUser ? 'cover' : 'contain'}
      />
      <div
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          maxWidth: compact ? 'calc(100% - 34px)' : '80%',
          padding: compact ? '0.55rem 0.7rem' : '0.75rem 1rem',
          borderRadius: isUser
            ? '12px 12px 2px 12px'
            : '12px 12px 12px 2px',
          background: isUser
            ? 'var(--color-primary)'
            : compact
              ? 'var(--color-surface)'
              : 'var(--color-bg)',
          color: isUser ? '#fff' : 'var(--color-text)',
          fontSize: compact ? '0.8125rem' : 'var(--font-size-sm)',
          lineHeight: 1.5,
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          boxShadow: compact && !isUser ? '0 1px 4px rgba(15, 28, 51, 0.06)' : undefined,
          border: compact && !isUser ? '1px solid var(--color-border-light)' : undefined,
        }}
      >
        {isTyping ? (
          <div style={{ display: 'flex', gap: '0.25rem', padding: '0.25rem 0' }}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'var(--color-text-muted)',
                  animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </div>
        ) : isUser ? (
          content
        ) : (
          <div style={{ lineHeight: 1.6 }}>
            <ReactMarkdown
              components={{
                p: ({ children }) => <p style={{ margin: '0.25rem 0' }}>{children}</p>,
                ul: ({ children }) => <ul style={{ paddingLeft: '1.25rem', margin: '0.25rem 0' }}>{children}</ul>,
                ol: ({ children }) => <ol style={{ paddingLeft: '1.25rem', margin: '0.25rem 0' }}>{children}</ol>,
                code: ({ children }) => (
                  <code
                    style={{
                      background: 'var(--color-border-light)',
                      padding: '0.125rem 0.375rem',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.8em',
                    }}
                  >
                    {children}
                  </code>
                ),
                strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
              }}
            >
              {content}
            </ReactMarkdown>
            <JuliaSources metadata={metadata} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
